import React, { useCallback, useMemo, useState } from "react";
import { Modal, Col, Row, Card } from "antd";
import Position from "../../../../../../models/Position";
// import WarehouseService from "../../../../../../services/WarehouseService";
// import CorridorService from "../../../../../../services/CorridorService";
import { CorridorShow } from "../../../../../../models/Corridor";
import ImageDisplayer from "../../../../../utils/ImageDisplayer";
import { WarehousePictureService } from "../../../../../../services/PictureService";
import { WarehouseThumbnailService } from "../../../../../../services/ThumbnailService";
import { useLanguage } from "../../../../../../context/LanguageContext";

const MAP_CELL_MIN_SIZE = 12;
const MAP_CELL_MAX_SIZE = 20;

// Modal component to display images
const ImageModal: React.FC<ImageModalProps> = ({
  visible,
  warehouseId,
  modalPosition,
  onClose,
}) => {
  const { language } = useLanguage();

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      destroyOnClose={true}
      title={
        language === "es"
          ? `Posición: ${modalPosition}`
          : `Position: ${modalPosition}`
      }
    >
      <Row justify="space-around" align="middle">
        <Col span={"100px"}>
          <Card style={{ width: "230px", textAlign: "center", height: "100" }}>
            <ImageDisplayer
              fetchFunction={() => {
                const paddedPositionLabel = modalPosition.padStart(5, " ");
                return WarehousePictureService.getWarehousePositionPicture(
                  warehouseId,
                  paddedPositionLabel,
                );
              }}
              lowfetchFuntion={() => {
                const paddedPositionLabel = modalPosition.padStart(5, " ");
                return WarehouseThumbnailService.getWarehousePositionThumbnail(
                  warehouseId,
                  paddedPositionLabel,
                );
              }}
              height={"100px"}
            />
          </Card>
        </Col>
        <Col span={"100px"}>
          <Card style={{ width: "230px", textAlign: "center", height: "100" }}>
            <ImageDisplayer
              fetchFunction={() => {
                const paddedPositionLabel = modalPosition.padStart(5, " ");
                return WarehousePictureService.getWarehousePalletPicture(
                  warehouseId,
                  paddedPositionLabel,
                );
              }}
              lowfetchFuntion={() => {
                const paddedPositionLabel = modalPosition.padStart(5, " ");
                return WarehouseThumbnailService.getWarehousePalletThumbnail(
                  warehouseId,
                  paddedPositionLabel,
                );
              }}
              height={"100px"}
            />
          </Card>
        </Col>
      </Row>
    </Modal>
  );
};
interface ImageModalProps {
  visible: boolean;
  warehouseId: number;
  modalPosition: string;
  onClose: () => void;
}
interface GridProps {
  warehouseId: string;
  corridorInfo: CorridorShow;
  isMobile: boolean;
  corridorPositions: Position[];
}
type ButtonSize = "small" | "middle" | "large";
const CorridorMap: React.FC<GridProps> = ({
  corridorInfo,
  warehouseId,
  isMobile,
  corridorPositions,
}) => {
  const { language } = useLanguage();
  const positions = corridorPositions;
  const corridor = corridorInfo;
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [modalPosition, setModalPosition] = useState<string>("");

  const buttonSize: ButtonSize = isMobile ? "large" : "small";
  const cellGap = buttonSize === "large" ? 3 : 2;
  const positionsLookup = useMemo(() => {
    const lookup: Record<string, Position> = {};
    for (const position of positions) {
      lookup[position.position_label] = position;
    }
    return lookup;
  }, [positions]);
  // const updateContent = useCallback(() => {
  //   setLoading(true)
  //   CorridorService.getOne(corridorId.toString()).then((corridor) => {
  //     if (corridor === null) {
  //       return;
  //     }
  //     WarehouseService.getPositionsByCorridor(warehouseId, corridor.name).then(
  //       (positions) => {
  //         if (positions === null || corridor === undefined) {
  //           return;
  //         }
  //         setCorridor(corridor);
  //         setPositions(positions);
  //         setLoading(false)
  //       }
  //     );
  //   });
  // }, [warehouseId, corridorId]);

  // useEffect(() => {
  //   updateContent();
  //   const interval = setInterval(() => {
  //     updateContent();
  //   }, 600000);
  //   return () => clearInterval(interval);
  // }, [updateContent]);

  const handlePopoverClick = useCallback((position: Position | undefined) => {
    if (position) {
      setModalPosition(position.position_label);
      setModalVisible(true);
    }
  }, []);

  const columnCount = useMemo(() => {
    if (!corridor?.rack_template?.column_range) {
      return 0;
    }
    return (
      corridor.rack_template.column_range[1] -
      corridor.rack_template.column_range[0] +
      1
    );
  }, [corridor]);

  const levelIndexes = useMemo(() => {
    if (!corridor?.rack_template?.level_range) {
      return [] as number[];
    }
    const levels =
      corridor.rack_template.level_range[1] -
      corridor.rack_template.level_range[0] +
      1;
    return Array.from({ length: levels }, (_, i) => i).reverse();
  }, [corridor]);

  const computedCellSize = useMemo(() => {
    if (columnCount <= 0) {
      return MAP_CELL_MAX_SIZE;
    }
    const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1280;
    const estimatedFrameWidth = Math.max(320, viewportWidth - (isMobile ? 56 : 300));
    const totalGap = (columnCount - 1) * cellGap;
    const available = Math.floor((estimatedFrameWidth - totalGap) / columnCount);
    return Math.max(MAP_CELL_MIN_SIZE, Math.min(MAP_CELL_MAX_SIZE, available));
  }, [columnCount, cellGap, isMobile]);

  return (
    <Row
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "flex-start",
        width: "100%",
        overflow: "hidden",
      }}
    >
      {corridor &&
        corridor.rack_template &&
        levelIndexes.map((levelIndex) => (
          <div
            key={`row-${levelIndex}`}
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${columnCount}, ${computedCellSize}px)`,
              gap: `${cellGap}px`,
              width: "max-content",
              maxWidth: "100%",
            }}
          >
            {corridor?.rack_template &&
              Array.from(
                {
                  length:
                    corridor.rack_template.column_range[1] -
                    corridor.rack_template.column_range[0] +
                    1,
                },
                (_, j) => {
                  let colText = corridor.rack_template?.column_range
                    ? String(
                        j + corridor.rack_template.column_range[0],
                      ).padStart(2, "0")
                    : "";
                  if (corridor.rack_template?.orientation === "right") {
                    colText = corridor.rack_template?.column_range
                      ? String(
                          corridor.rack_template.column_range[1] - j,
                        ).padStart(2, "0")
                      : "";
                  }
                  const levelText = corridor.rack_template?.level_range
                    ? String(levelIndex + corridor.rack_template.level_range[0])
                    : "";
                  const positionText = corridor.name + colText + levelText;
                  const matchingPosition = positionsLookup[positionText];
                  var palletLabel =
                    matchingPosition?.pallet_label ??
                    (language === "es" ? "Vacía" : "Empty Position");
                  palletLabel =
                    matchingPosition?.state === "unknown"
                      ? language === "es"
                        ? "Sin explorar"
                        : "Unexplored"
                      : palletLabel;
                  const popoverContent = matchingPosition
                    ? language === "es"
                      ? `Etiqueta de pallet: ${palletLabel}`
                      : `Pallet Label: ${palletLabel}`
                    : "";
                  const buttonStyle = {
                    backgroundColor: "#FF5252",
                    width: "100%",
                    aspectRatio: "1 / 1",
                    minWidth: 0,
                    border: "1px solid #1677ff",
                    borderRadius: "4px",
                    cursor: matchingPosition ? "pointer" : "default",
                    padding: 0,
                  };
                  if (matchingPosition) {
                    switch (matchingPosition.state) {
                      case "full":
                        buttonStyle.backgroundColor = "#4CAF50";
                        break;
                      case "empty":
                        buttonStyle.backgroundColor = "#a0d911";
                        break;
                      case "unknown":
                        buttonStyle.backgroundColor = "#FF5252";
                        break;
                      case "invalid":
                        buttonStyle.backgroundColor = "#FFC107";
                        break;
                      default:
                        buttonStyle.backgroundColor = "#bfbfbf";
                        break;
                    }
                  } else {
                    buttonStyle.backgroundColor = "#bfbfbf";
                  }
                  return (
                    <button
                      type="button"
                      key={`button-${levelIndex}-${j}`}
                      style={buttonStyle}
                      title={
                        !isMobile
                          ? `${language === "es" ? "Posición" : "Position"}: ${positionText}\n${popoverContent}`
                          : ""
                      }
                      onClick={() => handlePopoverClick(matchingPosition)}
                    ></button>
                  );
                },
              )}
          </div>
        ))}
      <ImageModal
        visible={modalVisible}
        warehouseId={parseInt(warehouseId)}
        modalPosition={modalPosition}
        onClose={() => setModalVisible(false)}
      />
    </Row>
  );
};

export default CorridorMap;
